import Link from "next/link";

import { HotelListType } from "../../../types/HotelType";
import { getHotelSlug } from "../../../utils/slugUtils";
import { HotelListModal } from "../../Hotels/HotelListModal";
import { BookingSiteOperated } from '../../Misc/BookingSiteOperated/BookingSiteOperated';
import styles from "./ListHotelItem.module.scss";
import { ListHotelImageCarousel } from "./ListHotelImageCarousel/ListHotelImageCarousel";
import { useContext, useState } from 'react';
import ListHotelPopUp from "./ListHotelPopUp/ListHotelPopUp";
import { DatesSearchContext, DatesSearchContextType } from "../../../contexts/DatesSearchContext";
import { differenceInDays } from "date-fns";

// CRO-1240
import { ABContext } from "../../../contexts/ABContext";
import { PricePerDate } from "../../Misc/PricePerDate/PricePerDate";

type ListHotelItemProps = {
  hotel: HotelListType;
  hotelTitleInCart: string[],
  roomsPage: boolean,
}

export function ListHotelItem({hotel, hotelTitleInCart}:  ListHotelItemProps ) {
  const [loading, setLoading] = useState<boolean>(false);

  const { datesSearch } = useContext<DatesSearchContextType>(DatesSearchContext);
  const [ stayLength ] = useState<number>(differenceInDays(datesSearch[1],datesSearch[0]))

  const handleButtonClick = () => {
    setLoading(true);
  }

  const buttonText = 'View Rooms';

  const viewRoomsButton = <Link href={{ pathname: `/hotels/${getHotelSlug(hotel.title)}` }}>
    {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */
    }<button onClick={handleButtonClick} className={styles.btn}>{buttonText}</button></Link>

  const hotelSelectedButton = <Link href={{ pathname: `/hotels/${getHotelSlug(hotel.title)}` }}>
    {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */
    }<button onClick={handleButtonClick} className={styles.selected}>Selected</button></Link>

  const hotelChangeButton = <ListHotelPopUp viewRoomsButton={viewRoomsButton} showPopup={<button onClick={handleButtonClick} className={styles.btn}>{buttonText}</button>} />

  // "KSPS" are a few facilities from the api
  const facilities: { name: string, kspClass: string}[] = [];
  const facilitiesWithParagraphs: { name: string, kspClass: string, paragraph: string }[] = [];

  hotel.facilities.forEach(facility => {
    if (facility.status === 1) {
      if (facility.name === "Early Park Admission") {
        facilitiesWithParagraphs.push({ ...facility, kspClass: 'kspEarly', paragraph: '(Valid theme park admission required. Select attractions)' });
      }
      if (facility.name === "Themed Kid's Suites") {
        facilities.push({ ...facility, kspClass: 'kspKids' })
      }
      if (facility.name === "Free Universal Express Unlimited") {
        facilitiesWithParagraphs.push({ ...facility, kspClass: 'kspExpress', paragraph: 'Skip the regular lines. Theme park admission required' })
      }
      if (facility.name === "Club Level Rooms") {
        facilities.push({ ...facility, kspClass: 'kspClub' });
      }
      if (facility.name === "Relax in Luxury at Mandara Spa") {
        facilities.push({ ...facility, kspClass: 'kspSpa' });
      }
      if (facility.name === "Water Taxi Park Transportation") {
        facilities.push({ ...facility, kspClass: 'kspTaxi' });
      }
      if (facility.name === "Dedicated Entrance to Epic Universe") {
        facilitiesWithParagraphs.push({ ...facility, kspClass: 'kspEpicEntrance', paragraph: '(Valid theme park admission required.)' });
      }
    }
  });

  const button = () => {

    if (hotelTitleInCart.includes(hotel.title)) return hotelSelectedButton

    if (hotelTitleInCart.length > 0 && !hotelTitleInCart.includes(hotel.title)) return hotelChangeButton

    return viewRoomsButton
  }

  const getCategoryText = (categoryText: string) => {
    if(categoryText === "Value"){
      return "Value Inns and Suites";
    } else if (categoryText === "Signature") {
      return "Signature Collection"
    } else if (categoryText === "Prime") {
      return "Prime Value Hotels"
    } else {
      return categoryText;
    }
  }

  // CRO-1240-Price-per-night
  const ab = useContext(ABContext);
  const isCro1240 = ab.isActiveTest('CRO-1240-Price-per-night');

  return (
    <div key={hotel.id} className={styles.listItemCalendarTicket}>
      <div className={styles.product}>
        <div className={styles.productImage}>
          <span className={styles.tag}>
            {getCategoryText(hotel.category)}
          </span>
          <div className={styles.img}>
            <ListHotelImageCarousel photos={hotel.photos} />
          </div>
        </div>
        <div className={styles.productDetails}>
          <h3>
            <strong>{hotel.title} </strong>
            <em className={styles.stars} style={{width : Number(hotel.star_rating) * 20}}>
              <span className={styles.visuallyHidden}>{hotel.star_rating} star hotel</span>
            </em>
          </h3>
          <div className={styles.ksps}>
            {/* Adding facilities with paragraphs first */}
            {facilitiesWithParagraphs.length > 0 && facilitiesWithParagraphs.map((facility, i) => {
              return (
                <div key={i} className={`${styles.ksp} ${styles[facility.kspClass]}`}>
                  <h4>{facility.name}</h4>
                  {facility.paragraph && <p>{facility.paragraph}</p>}
                </div>
              )
            })}
            {facilities.length > 0 && facilities.map((facility, i) => {
              return (
                <div key={i} className={`${styles.ksp} ${styles[facility.kspClass]}`}>
                  <h4>{facility.name}</h4>
                </div>
              )
            })}
          </div>
          <p className={styles.descriptionText}>{hotel.desc_short}</p>
          <HotelListModal hotel={hotel} roomsPage={false} />
        </div>
      </div>
      <div className={`${styles.footer} ${styles.hotelFooter}`}>
        <div className={styles.footerPricingCta}>
            {+hotel.price > 0 ? (
              <>
            <div className={styles.productPricing}>
              {isCro1240 ?
                <>
                  <span className={styles.from}>Starting from</span>
                  <PricePerDate totalPrice={hotel.price} numOfDates={stayLength} productType="hotel"/>
                </>
              :
                <p className={styles.priceStyles}>
                  <span className={styles.from}>Starting from</span>
                  <strong className={styles.fromPrice}>£{hotel.price}</strong>
                  <span className={styles.total}>Total for {stayLength}-night stay</span>
                  <em className={styles.textBelow}>Includes taxes and charges</em>
                </p>
              }
              </div>
              <div className={styles.callToAction}>
                {!loading ? (
                  <>
                    {button()}
                  </>
                ):
                <button disabled className={styles.btn}>Loading...</button>
                }
              </div>
              </>
          ) : ''
          }
        </div>
        <BookingSiteOperated placement="hotel" />
      </div>
    </div>
  );
}

